/**
 * ESTABLISHMENT-CONFIG
 */
import Card from "@/components/card"
import { t } from "i18next"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useState, useMemo } from "react"
import establishmentStore from "../model/establishment.store"
import { createEmptyEstablishment, Establishment } from "../model/establishment.types"
import EstablishmentDeleteModal from "./modal/EstablishmentDeleteModal"
import EstablishmentContributors from "./EstablishmentContributors"
import EstablishmentCard from "./EstablishmentCard"
import { userEdit } from "@/redux/user.actions"
import EstablishmentFormModal from "./modal/EstablishmentFormModal"
import EstablishmentCreateMethodModal from "./modal/EstablishmentCreateMethodModal"
import EstablishmentSyncModal from "./modal/EstablishmentSyncModal"
import { toast } from "react-toastify"
import PageHeader from "@/components/page-header"
import Button from "@/components/button"
import SearchBar from "@/components/search-bar"
import Space from "@/components/space"
import ToggleButton from "@/components/toggle-button"
import { establishmentsNavItem } from "@/nav/nav.app"
import PageLoader from "@/components/page-loader"
import { StatusType } from "@/redux/_status.types"
import { useNavigate } from "react-router-dom"

interface Props {
  loadStatus: StatusType
  onRefresh: () => void
}

export default function EstablishmentsConfig({ loadStatus, onRefresh }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const establishments = useAppSelector(establishmentStore.selectors.list)
  const active = useAppSelector(establishmentStore.selectors.active)
  const draft = useAppSelector(establishmentStore.selectors.draft)
  const status = useAppSelector(establishmentStore.selectors.status)
  const _session = useAppSelector((state) => state._session)

  const [modal, setModal] = useState<null | "CREATE_METHOD" | "SYNC" | "EDIT" | "ADD">(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [showInactive, setShowInactive] = useState(false)

  const filteredEstablishments = useMemo(() => {
    const filteredByStatus = showInactive
      ? establishments.filter((e) => e.isDisabled)
      : establishments.filter((e) => !e.isDisabled)
    if (!search.trim()) return filteredByStatus
    const q = search.toLowerCase()
    return filteredByStatus.filter(
      (e) => e.name?.toLowerCase().includes(q) || e.codeEstablishment?.toLowerCase().includes(q)
    )
  }, [establishments, search, showInactive])

  const handleSave = async () => {
    if (!draft.name) {
      toast(t("Merci de remplir le nom de l'établissement"))
      return
    }

    const isUpdate = establishments.some((e) => e.id === draft.id)

    let establishment: Establishment
    if (!isUpdate) {
      establishment = await dispatch(establishmentStore.thunks.createEntity(draft)).unwrap()

      if (_session.risksInterfaceType === "ESTABLISHMENT" && !_session.establishmentId) {
        const connection = await dispatch(establishmentStore.thunks.enterContext(establishment)).unwrap()
        if (connection?.error) {
          toast(t("L'établissement a été créé, mais la connexion à celui-ci a échoué."), { type: "error" })
          setModal(null)
          onRefresh()
          return
        }

        setModal(null)
        navigate("/home")
        return
      }
    } else {
      establishment = await dispatch(establishmentStore.thunks.updateEntity()).unwrap()
      store.dispatch(userEdit("EstablishmentId", establishment.id))
      dispatch(establishmentStore.actions.replace(establishment))
    }

    dispatch(establishmentStore.actions.setActive(null))
    setModal(null)
  }

  const confirmDelete = async () => {
    if (!draft?.id) return
    try {
      await dispatch(establishmentStore.thunks.removeEntity(draft.id)).unwrap()
    } catch {
      //Le client API affiche deja le message metier renvoye par le backend. La modale reste ouverte.
      return
    }
    dispatch(establishmentStore.actions.setActive(createEmptyEstablishment()))
    dispatch(establishmentStore.actions.cancel())
    setIsDeleteModalOpen(false)
  }

  const cancelDelete = async () => {
    dispatch(establishmentStore.actions.setActive(createEmptyEstablishment()))
    setIsDeleteModalOpen(false)
  }

  const handleCreateNewEstablishment = () => {
    setModal("CREATE_METHOD")
  }

  const handleCreateManually = () => {
    setModal("ADD")
    const establishment = createEmptyEstablishment()
    dispatch(establishmentStore.actions.setActive(establishment))
  }

  const handleEdit = (establishment: Establishment) => {
    setModal("EDIT")
    dispatch(establishmentStore.actions.setActive(establishment))
  }

  return (
    <>
      <PageHeader
        title={establishmentsNavItem.text}
        subtitle="Gérez vos établissements et invitez des contributeurs à compléter leur document unique.">
        <Button className="primary" onClick={() => handleCreateNewEstablishment()}>
          Ajouter un établissement
        </Button>
      </PageHeader>

      <PageLoader status={loadStatus} onRefresh={onRefresh} forceLoading>
        <div className="flex">
          <SearchBar onSearch={(value: string) => setSearch(value)} />
          <Space />
          <div className="flex items-center gap-2">
            <span className="grey-t whitespace-nowrap">{t("Afficher les inactifs")}</span>
            <ToggleButton isActive={showInactive} onToogle={() => setShowInactive((value) => !value)} />
          </div>
        </div>
        <div className="h-20" />

        {establishments.length > 0 ? (
          <EstablishmentDeleteModal
            isOpen={isDeleteModalOpen}
            name={active?.name}
            status={status}
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
          />
        ) : (
          <Card>
            <div className="grey-t font-bold">{t("Vous n'avez créé aucun établissement pour le moment.")}</div>
          </Card>
        )}

        {!isDeleteModalOpen && modal === "CREATE_METHOD" && (
          <EstablishmentCreateMethodModal
            onManual={handleCreateManually}
            onSync={() => setModal("SYNC")}
            onClose={() => setModal(null)}
          />
        )}

        {!isDeleteModalOpen && modal === "SYNC" && <EstablishmentSyncModal onClose={() => setModal(null)} />}

        {!isDeleteModalOpen && (modal === "ADD" || modal === "EDIT") && (
          <EstablishmentFormModal
            isNew={modal === "ADD"}
            status={status}
            onSave={handleSave}
            onClose={() => setModal(null)}
          />
        )}

        {filteredEstablishments.length > 0 &&
          filteredEstablishments.map((e) => (
            <EstablishmentCard
              key={e.id}
              establishment={e}
              showConnectButton
              onDelete={setIsDeleteModalOpen}
              onEdit={() => handleEdit(e)}>
              <EstablishmentContributors establishment={e} />
            </EstablishmentCard>
          ))}
      </PageLoader>
    </>
  )
}
